import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { studentNav } from "@/config/nav";
import { StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Clock, Award, FileText, Loader2, Play, Eye, RotateCcw } from "lucide-react";

export default function StudentExams() {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/exams");
    setExams(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const start = async (id) => {
    setStarting(id);
    try {
      const { data } = await api.post(`/exams/${id}/start`);
      navigate(`/app/attempt/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start exam");
      setStarting(null);
    }
  };

  return (
    <DashboardLayout nav={studentNav} title="Examinations">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Available Examinations</h1>
        <p className="text-muted-foreground mt-1">Click start to begin — the timer starts immediately.</p>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
      ) : exams.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <BookOpen className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">No examinations available right now.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border border border-border">
          {exams.map((ex) => {
            const exhausted = ex.my_attempts >= ex.attempts_allowed;
            const inProgress = ex.my_status === "in_progress";
            const isReattempt = ex.my_attempts > 0 && !exhausted;
            const busy = starting === ex.id;
            return (
              <div key={ex.id} className="bg-card p-6 flex flex-col" data-testid={`exam-card-${ex.id}`}>
                <div className="flex items-center justify-between">
                  <StatusBadge status={ex.my_status} />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{ex.subject}</span>
                </div>
                <h3 className="mt-3 text-lg font-semibold leading-tight">{ex.title}</h3>
                {ex.description && <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{ex.description}</p>}
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-mono">
                  <span className="flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {ex.question_count} Q</span>
                  <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {ex.duration_minutes}m</span>
                  <span className="flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {ex.total_marks} marks</span>
                  <span>{ex.my_attempts}/{ex.attempts_allowed} used</span>
                </div>
                <div className="mt-5 pt-4 border-t border-border flex gap-2">
                  {inProgress ? (
                    <Button className="flex-1 rounded-sm" onClick={() => navigate(`/app/attempt/${ex.my_attempt_id}`)} data-testid={`resume-${ex.id}`}>
                      <Play className="h-4 w-4 mr-1.5" /> Resume
                    </Button>
                  ) : exhausted ? (
                    ex.my_attempt_id ? (
                      <Button variant="outline" className="flex-1 rounded-sm" onClick={() => navigate(`/app/results/${ex.my_attempt_id}`)} data-testid={`viewresult-${ex.id}`}>
                        <Eye className="h-4 w-4 mr-1.5" /> View result
                      </Button>
                    ) : (
                      <Button variant="outline" className="flex-1 rounded-sm" disabled>No attempts left</Button>
                    )
                  ) : (
                    <Button className="flex-1 rounded-sm" onClick={() => start(ex.id)} disabled={busy} data-testid={`start-${ex.id}`}>
                      {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : isReattempt ? <RotateCcw className="h-4 w-4 mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}
                      {isReattempt ? "Reattempt" : "Start exam"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
